import BaseService from '@/lib/BaseService'
import { DtsApiResponse } from '@/types/dts'
import store from '@/store'

export interface DetailsTabStripsResponse {
    value: string
    label: string
    /**
     * Canonical, globally-unique, space-free object name (e.g. "Admin_User_List").
     * Used to resolve a tab unambiguously — see resolveDtsIdByName.
     */
    objName: string
    icon?: string
    order: number
    isVisible: boolean
    isEnabled: boolean
    dtsId: number
    dtsParentId?: number
    isMMSEnabled?: boolean
}

export const detailsTabStripsService = {
    /**
     * Get details tab strips from the API
     * @param mmsId - The MMS ID to fetch tabs for
     * @param languageId - Optional language ID (defaults to user's language from store)
     * @param baId - Optional BA ID (defaults to user's BA from store)
     * @returns Promise with array of DTS items
     */
    getDetailsTabStrips: async (
        mmsId: number,
        languageId?: number,
        baId?: number,
    ): Promise<DetailsTabStripsResponse[]> => {
        try {
            // Get languageId and baId from Redux store if not provided
            const state = store.getState()
            const finalLanguageId = languageId || state.auth.user.languageId
            const finalBaId = baId || state.auth.user.baId

            // Build query parameters
            const params = new URLSearchParams()
            if (finalLanguageId)
                params.append('languageId', finalLanguageId.toString())
            if (finalBaId) params.append('baId', finalBaId.toString())
            if (mmsId) params.append('mmsId', mmsId.toString())

            // Make API request
            const response = await BaseService.get(`/api/screen/dts`, {
                params,
            })

            // Transform the API response
            return transformDtsData(response.data)
        } catch (error) {
            console.error('Error fetching DTS data:', error)
            throw error
        }
    },

    /**
     * Get children DTS items for a given parent dtsId
     * @param allTabs - All available DTS tabs
     * @param parentDtsId - The parent DTS ID to get children for (null/undefined for root level)
     * @returns Array of child DTS items for the given parent
     */
    getChildDtsList: (
        allTabs: DetailsTabStripsResponse[],
        parentDtsId?: number | null,
    ): DetailsTabStripsResponse[] => {
        return allTabs.filter((tab) => {
            if (parentDtsId === null || parentDtsId === undefined) {
                // Root level: show tabs with no parent or parent is not in the list
                return (
                    !tab.dtsParentId ||
                    !allTabs.some((t) => t.dtsId === tab.dtsParentId)
                )
            }
            // Child level: show tabs whose parent matches
            return tab.dtsParentId === parentDtsId
        })
    },

    /**
     * Check if a DTS item has children
     * @param allTabs - All available DTS tabs
     * @param dtsId - The DTS ID to check
     * @returns True if the item has children
     */
    hasChildren: (
        allTabs: DetailsTabStripsResponse[],
        dtsId: number,
    ): boolean => {
        return allTabs.some((tab) => tab.dtsParentId === dtsId)
    },

    /**
     * Get the parent DTS item for a given dtsId
     * @param allTabs - All available DTS tabs
     * @param dtsId - The DTS ID to get parent for
     * @returns Parent DTS item or null if no parent
     */
    getParentDts: (
        allTabs: DetailsTabStripsResponse[],
        dtsId: number,
    ): DetailsTabStripsResponse | null => {
        const item = allTabs.find((tab) => tab.dtsId === dtsId)
        if (!item?.dtsParentId) return null
        return allTabs.find((tab) => tab.dtsId === item.dtsParentId) || null
    },

    /**
     * Get the breadcrumb path for a DTS item (from root to item)
     * @param allTabs - All available DTS tabs
     * @param dtsId - The DTS ID to get path for
     * @returns Array of DTS items from root to the item
     */
    getDtsBreadcrumb: (
        allTabs: DetailsTabStripsResponse[],
        dtsId: number,
    ): DetailsTabStripsResponse[] => {
        const breadcrumb: DetailsTabStripsResponse[] = []
        let currentDtsId: number | undefined = dtsId

        while (currentDtsId) {
            const item = allTabs.find((tab) => tab.dtsId === currentDtsId)
            if (!item) break
            breadcrumb.unshift(item)
            currentDtsId = item.dtsParentId
        }

        return breadcrumb
    },

    /**
     * Get the complete parent chain for a dtsId (from root to the tab itself)
     * Returns array of dtsIds in order from root to the target tab
     * @param allTabs - All available DTS tabs
     * @param dtsId - The DTS ID to get hierarchy path for
     * @returns Array of dtsIds from root to target
     */
    getDtsHierarchyPath: (
        allTabs: DetailsTabStripsResponse[],
        dtsId: number,
    ): number[] => {
        let currentTab = allTabs.find((tab) => tab.dtsId === dtsId)

        if (!currentTab) return []

        // Build path from target to root
        const reversePath: number[] = [currentTab.dtsId]
        while (currentTab?.dtsParentId != null) {
            reversePath.push(currentTab.dtsParentId)
            currentTab = allTabs.find(
                (tab) => tab.dtsId === currentTab?.dtsParentId,
            )
        }

        // Reverse to get root to target order
        return reversePath.reverse()
    },

    /**
     * Get the immediate parent dtsId for a given dtsId
     * @param allTabs - All available DTS tabs
     * @param dtsId - The DTS ID to get parent for
     * @returns Parent dtsId or null if at root level
     */
    getImmediateParentDtsId: (
        allTabs: DetailsTabStripsResponse[],
        dtsId: number,
    ): number | null => {
        const tab = allTabs.find((t) => t.dtsId === dtsId)
        return tab?.dtsParentId ?? null
    },
}

/**
 * Transform DTS API response to the format expected by the component
 * @param apiData - The raw API response (can be string or array)
 * @returns Transformed DTS data
 */
const transformDtsData = (
    apiData: DtsApiResponse[] | string,
): DetailsTabStripsResponse[] => {
    // If the API data is a string, parse it into an object
    let parsedData: DtsApiResponse[]

    if (typeof apiData === 'string') {
        try {
            parsedData = JSON.parse(apiData)
        } catch (error) {
            console.error('Failed to parse DTS data:', error)
            return []
        }
    } else {
        parsedData = apiData
    }

    // Transform each DTS item to the expected format
    return parsedData.map((item, index) => ({
        value: `tab${index + 1}`,
        label: item.DtsName,
        objName: item.DtsObjName,
        icon: undefined, // Icons can be added later based on IconId if needed
        order: item.DtsOrder,
        isVisible: item.DtsVisible,
        isEnabled: item.DtsEnabled,
        dtsId: item.DTSId,
        // Normalize parentId: treat 0 as null (0 means root level in backend)
        dtsParentId: item.DtsParentId === 0 ? undefined : item.DtsParentId,
        isMMSEnabled: item.MMSEnabled,
    }))
}

export default detailsTabStripsService
